import React from 'react';
import {
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Add,
    Cancel,
    CheckCircle,
    Delete,
    Edit,
    ExpandLess,
    ExpandMore,
    Save,
} from '@mui/icons-material';
import { useCanAccess } from 'react-admin';
import { ProjectFlowPhase } from '@/types';
import {
    phaseStatusColor,
    stepStatusLabel,
    nextStatus,
    formatFlowDate,
} from './projectFlowUtils';
import {
    useCreateFlowStep,
    useDeleteFlowStep,
    useUpdateFlowStep,
} from './useProjectFlow';
import { StepComments } from './StepComments';
import { CommentComposer } from './CommentComposer';

interface FieldsTimelineProps {
    projectId: number;
    phase: ProjectFlowPhase;
    currentUserId?: number;
}

// `undefined` = dialog closed; `null` = append at end; number = insert after step id.
type AddTarget = number | null | undefined;

export const FieldsTimeline: React.FC<FieldsTimelineProps> = ({
    projectId,
    phase,
    currentUserId,
}) => {
    const steps = phase.steps;
    const { canAccess: canUpdate } = useCanAccess({
        resource: 'project_flow',
        action: 'update',
    });
    const updateStepMutation = useUpdateFlowStep();
    const createStepMutation = useCreateFlowStep();
    const deleteStepMutation = useDeleteFlowStep();
    const [expandedStepId, setExpandedStepId] = React.useState<number | null>(null);
    const [editingStepId, setEditingStepId] = React.useState<number | null>(null);
    const [editingName, setEditingName] = React.useState<string>('');
    const [addTarget, setAddTarget] = React.useState<AddTarget>(undefined);
    const [newStepName, setNewStepName] = React.useState('');
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    // Auto-expand the current step the first time steps load. Subsequent
    // refetches (e.g. after posting a comment) must not re-override the user's
    // current expansion.
    const didAutoExpandRef = React.useRef(false);
    React.useEffect(() => {
        if (didAutoExpandRef.current || steps.length === 0) return;
        const currentStep = steps.find((s) => s.status !== 'done');
        if (currentStep) {
            setExpandedStepId(currentStep.id);
        }
        didAutoExpandRef.current = true;
    }, [steps]);

    const getStatusIcon = (status: string, position: number) => {
        if (status === 'done') {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: 'success.main',
                        color: 'white',
                        fontWeight: 'bold',
                    }}
                >
                    <CheckCircle sx={{ fontSize: 24 }} />
                </Box>
            );
        }
        if (status === 'in_progress') {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        color: 'white',
                        fontWeight: 'bold',
                    }}
                >
                    {position}
                </Box>
            );
        }
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    border: '2px solid',
                    borderColor: 'action.disabled',
                    color: 'text.disabled',
                    fontWeight: 'bold',
                }}
            >
                {position}
            </Box>
        );
    };

    const handleStatusChange = (stepId: number, newStatus: string) => {
        updateStepMutation.mutate({
            projectId,
            stepId,
            data: { status: newStatus },
        });
    };

    const handleToggleExpand = (stepId: number) => {
        setExpandedStepId(expandedStepId === stepId ? null : stepId);
    };

    const handleRenameStep = (stepId: number, newName: string) => {
        if (!newName.trim()) {
            setEditingStepId(null);
            return;
        }
        updateStepMutation.mutate({
            projectId,
            stepId,
            data: { name: newName.trim() },
        });
        setEditingStepId(null);
    };

    const handleDeleteStep = (stepId: number) => {
        if (!window.confirm('Delete this step?')) return;
        deleteStepMutation.mutate({ projectId, stepId });
    };

    const closeAddDialog = () => {
        setAddTarget(undefined);
        setNewStepName('');
    };

    const handleAddStep = () => {
        const name = newStepName.trim();
        if (!name) return;
        createStepMutation.mutate(
            {
                projectId,
                data: {
                    phase: phase.key,
                    name,
                    after_step_id:
                        typeof addTarget === 'number' ? addTarget : undefined,
                },
            },
            { onSuccess: closeAddDialog }
        );
    };

    const renderInsertButton = (afterId: number | null, key: string) => {
        if (!canUpdate) return null;
        return (
            <Box
                key={key}
                sx={{
                    position: 'relative',
                    display: 'flex',
                    justifyContent: 'center',
                    py: { xs: 1, sm: 1.5 },
                }}
            >
                {/* Connector line passes behind the pill (desktop only) */}
                <Box
                    sx={{
                        display: { xs: 'none', sm: 'block' },
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        left: 24,
                        width: '2px',
                        bgcolor: 'action.disabled',
                    }}
                />
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Add sx={{ fontSize: 16 }} />}
                    onClick={() => setAddTarget(afterId)}
                    disabled={createStepMutation.isPending}
                    sx={{
                        borderRadius: 999,
                        bgcolor: 'background.paper',
                        textTransform: 'none',
                        px: 2,
                        py: 0.25,
                        zIndex: 1,
                    }}
                >
                    Insert step
                </Button>
            </Box>
        );
    };

    return (
        <Stack gap={1} sx={{ p: 2 }}>
            {steps.map((step, index) => {
                const position = index + 1;
                const isLast = index === steps.length - 1;
                // In the fields phase, even default steps can be renamed per project.
                const canEditStep = canUpdate;
                const isActive = step.status === 'in_progress';

                return (
                    <React.Fragment key={step.id}>
                        <Stack
                            direction="row"
                            gap={{ xs: 1.5, sm: 2 }}
                            sx={{ position: 'relative' }}
                        >
                            {/* Timeline marker column (hidden on mobile) */}
                            <Box
                                sx={{
                                    display: { xs: 'none', sm: 'flex' },
                                    justifyContent: 'center',
                                    alignItems: 'flex-start',
                                    minWidth: 40,
                                    pt: 0.5,
                                    zIndex: 1,
                                }}
                            >
                                {getStatusIcon(step.status, position)}
                            </Box>

                            {/* Step content */}
                            <Card
                                variant="outlined"
                                sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    p: { xs: 1.5, sm: 2 },
                                    borderColor: isActive ? 'primary.main' : 'divider',
                                    borderWidth: isActive ? 2 : 1,
                                }}
                            >
                                <Stack
                                    direction={{ xs: 'column', sm: 'row' }}
                                    justifyContent="space-between"
                                    alignItems={{ xs: 'stretch', sm: 'flex-start' }}
                                    gap={1}
                                    onClick={() =>
                                        editingStepId === step.id
                                            ? undefined
                                            : handleToggleExpand(step.id)
                                    }
                                    sx={{
                                        cursor:
                                            editingStepId === step.id
                                                ? 'default'
                                                : 'pointer',
                                    }}
                                >
                                    <Stack gap={0.5} flex={1} sx={{ minWidth: 0 }}>
                                        <Stack
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            gap={1}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{
                                                    textTransform: 'uppercase',
                                                    letterSpacing: 0.5,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Step {position}
                                            </Typography>
                                            <Chip
                                                label={stepStatusLabel(
                                                    step.status as any
                                                )}
                                                size="small"
                                                color={
                                                    phaseStatusColor(
                                                        step.status as any
                                                    ) as any
                                                }
                                                variant={
                                                    step.status === 'pending'
                                                        ? 'outlined'
                                                        : 'filled'
                                                }
                                            />
                                        </Stack>
                                            {editingStepId === step.id ? (
                                                <Stack
                                                    direction="row"
                                                    gap={0.5}
                                                    alignItems="center"
                                                >
                                                    <TextField
                                                        size="small"
                                                        fullWidth
                                                        value={editingName}
                                                        onChange={(e) =>
                                                            setEditingName(
                                                                e.target.value
                                                            )
                                                        }
                                                        autoFocus
                                                        onClick={(e) =>
                                                            e.stopPropagation()
                                                        }
                                                        onKeyDown={(e) => {
                                                            e.stopPropagation();
                                                            if (e.key === 'Enter') {
                                                                handleRenameStep(
                                                                    step.id,
                                                                    editingName
                                                                );
                                                            } else if (
                                                                e.key === 'Escape'
                                                            ) {
                                                                setEditingStepId(
                                                                    null
                                                                );
                                                            }
                                                        }}
                                                        disabled={
                                                            updateStepMutation.isPending
                                                        }
                                                    />
                                                    <Tooltip title="Save">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleRenameStep(
                                                                        step.id,
                                                                        editingName
                                                                    );
                                                                }}
                                                                disabled={
                                                                    updateStepMutation.isPending ||
                                                                    !editingName.trim()
                                                                }
                                                            >
                                                                <Save fontSize="small" />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Cancel">
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setEditingStepId(null);
                                                            }}
                                                            disabled={
                                                                updateStepMutation.isPending
                                                            }
                                                        >
                                                            <Cancel fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Stack>
                                            ) : (
                                                <Typography
                                                    variant="subtitle1"
                                                    fontWeight="bold"
                                                >
                                                    {step.name}
                                                </Typography>
                                            )}
                                            <Stack
                                                direction="row"
                                                gap={1}
                                                alignItems="center"
                                                flexWrap="wrap"
                                            >
                                                {step.started_at && (
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                    >
                                                        Started:{' '}
                                                        {formatFlowDate(
                                                            step.started_at
                                                        )}
                                                    </Typography>
                                                )}
                                                {step.completed_at && (
                                                    <Typography
                                                        variant="caption"
                                                        color="textSecondary"
                                                    >
                                                        Completed:{' '}
                                                        {formatFlowDate(
                                                            step.completed_at
                                                        )}
                                                    </Typography>
                                                )}
                                                {step.comment_count > 0 && (
                                                    <Chip
                                                        label={`${step.comment_count} comment${
                                                            step.comment_count !==
                                                            1
                                                                ? 's'
                                                                : ''
                                                        }`}
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={(e) =>
                                                            e.stopPropagation()
                                                        }
                                                    />
                                                )}
                                            </Stack>
                                        </Stack>

                                        {/* Status control + edit/delete */}
                                        <Stack
                                            direction="row"
                                            gap={{ xs: 0.5, sm: 1 }}
                                            alignItems="center"
                                            flexWrap="wrap"
                                            sx={{
                                                ml: { xs: 0, sm: 'auto' },
                                                justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                                            }}
                                        >
                                            {expandedStepId === step.id && (
                                                isSmallScreen ? (
                                                    <Select
                                                        size="small"
                                                        value={step.status}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            handleStatusChange(
                                                                step.id,
                                                                e.target.value
                                                            );
                                                        }}
                                                        onClick={(e) =>
                                                            e.stopPropagation()
                                                        }
                                                        disabled={
                                                            !canUpdate ||
                                                            updateStepMutation.isPending
                                                        }
                                                        sx={{ width: 120 }}
                                                    >
                                                        <MenuItem value="pending">
                                                            Pending
                                                        </MenuItem>
                                                        <MenuItem value="in_progress">
                                                            In Progress
                                                        </MenuItem>
                                                        <MenuItem value="done">
                                                            Done
                                                        </MenuItem>
                                                    </Select>
                                                ) : (
                                                    <Button
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleStatusChange(
                                                                step.id,
                                                                nextStatus(
                                                                    step.status as any
                                                                )
                                                            );
                                                        }}
                                                        disabled={
                                                            !canUpdate ||
                                                            updateStepMutation.isPending
                                                        }
                                                    >
                                                        {stepStatusLabel(
                                                            step.status as any
                                                        )}
                                                    </Button>
                                                )
                                            )}

                                            {canEditStep && expandedStepId === step.id && (
                                                <>
                                                    {editingStepId !== step.id && (
                                                        <Tooltip title="Rename step">
                                                            <IconButton
                                                                size={isSmallScreen ? 'large' : 'small'}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setEditingName(step.name);
                                                                    setEditingStepId(step.id);
                                                                }}
                                                                disabled={
                                                                    updateStepMutation.isPending
                                                                }
                                                                sx={{
                                                                    p: { xs: 1.25, sm: 0.5 },
                                                                }}
                                                            >
                                                                <Edit
                                                                    sx={{
                                                                        fontSize: {
                                                                            xs: 26,
                                                                            sm: 20,
                                                                        },
                                                                    }}
                                                                />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                    <Tooltip title="Delete step">
                                                        <IconButton
                                                            size={isSmallScreen ? 'large' : 'small'}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteStep(
                                                                    step.id
                                                                );
                                                            }}
                                                            disabled={
                                                                deleteStepMutation.isPending
                                                            }
                                                            sx={{
                                                                p: { xs: 1.25, sm: 0.5 },
                                                            }}
                                                        >
                                                            {deleteStepMutation.isPending &&
                                                            deleteStepMutation.variables?.stepId ===
                                                                step.id ? (
                                                                <CircularProgress
                                                                    size={isSmallScreen ? 22 : 16}
                                                                />
                                                            ) : (
                                                                <Delete
                                                                    sx={{
                                                                        fontSize: {
                                                                            xs: 26,
                                                                            sm: 20,
                                                                        },
                                                                    }}
                                                                />
                                                            )}
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            )}

                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleToggleExpand(step.id);
                                                }}
                                            >
                                                {expandedStepId === step.id ? (
                                                    <ExpandLess />
                                                ) : (
                                                    <ExpandMore />
                                                )}
                                            </IconButton>
                                        </Stack>
                                    </Stack>

                                    {/* Expanded content */}
                                    <Collapse
                                        in={expandedStepId === step.id}
                                        timeout="auto"
                                        unmountOnExit
                                    >
                                        <Stack
                                            gap={2}
                                            onClick={(e) => e.stopPropagation()}
                                            sx={{
                                                mt: 2,
                                                pt: 2,
                                                borderTop: '1px solid',
                                                borderColor: 'divider',
                                            }}
                                        >
                                            <StepComments
                                                projectId={projectId}
                                                stepId={step.id}
                                                comments={step.comments || []}
                                                totalCount={step.comment_count}
                                                currentUserId={currentUserId}
                                            />
                                            <CommentComposer
                                                projectId={projectId}
                                                stepId={step.id}
                                            />
                                        </Stack>
                                    </Collapse>
                                </Card>
                        </Stack>
                        {!isLast && renderInsertButton(
                            step.id,
                            `insert-after-${step.id}`
                        )}
                    </React.Fragment>
                );
            })}

            {/* Append step button (end-of-list) */}
            {canUpdate && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        pt: steps.length === 0 ? 4 : 1,
                        pb: steps.length === 0 ? 4 : 0,
                    }}
                >
                    <Button
                        variant="outlined"
                        startIcon={<Add />}
                        onClick={() => setAddTarget(null)}
                        disabled={createStepMutation.isPending}
                        sx={{ borderRadius: 999, textTransform: 'none' }}
                    >
                        {steps.length === 0 ? 'Add first step' : 'Add step'}
                    </Button>
                </Box>
            )}

            <Dialog
                open={addTarget !== undefined}
                onClose={closeAddDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Add step</DialogTitle>
                <DialogContent>
                    <Stack gap={2} sx={{ pt: 2 }}>
                        <TextField
                            fullWidth
                            label="Step name"
                            value={newStepName}
                            onChange={(e) => setNewStepName(e.target.value)}
                            disabled={createStepMutation.isPending}
                            autoFocus
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    handleAddStep();
                                }
                            }}
                        />
                        <Stack
                            direction="row"
                            gap={1}
                            justifyContent="flex-end"
                        >
                            <Button
                                onClick={closeAddDialog}
                                disabled={createStepMutation.isPending}
                                startIcon={<Cancel />}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleAddStep}
                                disabled={
                                    !newStepName.trim() ||
                                    createStepMutation.isPending
                                }
                                startIcon={<Add />}
                            >
                                Add step
                            </Button>
                        </Stack>
                    </Stack>
                </DialogContent>
            </Dialog>
        </Stack>
    );
};

FieldsTimeline.displayName = 'FieldsTimeline';
